import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Provider } from 'react-redux';
import { store } from './store';
import Landing from './pages/Landing';
import Login from './pages/Login';
import Register from './pages/Register';
import VerifyOtp from './pages/VerifyOtp';
import Dashboard from './pages/Dashboard';
import TestCSS from './pages/TestCSS';
import PurchaseOrders from './pages/PurchaseOrders/PurchaseOrdersList';
import CreatePO from './pages/PurchaseOrders/CreatePO';
import PODetails from './pages/PurchaseOrders/PODetails';
import Invoices from './pages/Invoices/InvoicesList';
import CreateInvoice from './pages/Invoices/CreateInvoice';
import InvoiceDetails from './pages/Invoices/InvoiceDetails';
import EditInvoice from './pages/Invoices/EditInvoice';
import Users from './pages/Users/UsersList';
import CreateUser from './pages/Users/CreateUser';
import EditUser from './pages/Users/EditUser';
import ActivityDetails from './pages/Activities/ActivityDetails';
import NotificationHistory from './pages/Notifications/NotificationHistory';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import InventoryList from './pages/Inventory/InventoryList';
import CreateInventory from './pages/Inventory/CreateInventory';
import EditInventory from './pages/Inventory/EditInventory';
import CompanySettings from './pages/Company/CompanySettings';
import RetailBillsList from './pages/RetailBills/RetailBillsList';
import CreateRetailBill from './pages/RetailBills/CreateRetailBill';
import ViewRetailBill from './pages/RetailBills/ViewRetailBill';
import SubscriptionManagement from './pages/Subscription/SubscriptionManagement';
import UpgradeSubscription from './pages/Subscription/UpgradeSubscription';

import Layout from './components/Layout/Layout';
import PrivateRoute from './components/Auth/PrivateRoute';
import ProtectedRoute from './components/ProtectedRoute';
import PermissionRoute from './components/Auth/PermissionRoute';
import { Permission } from './constants/permissions';

function App() {
  return (
    <Provider store={store}>
      <Router>
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/verify-otp" element={<VerifyOtp />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password/:token" element={<ResetPassword />} />
          
          <Route element={<PrivateRoute />}>
            <Route element={<Layout />}>
              {/* Dashboard - No subscription required */}
              <Route path="/dashboard" element={
                <PermissionRoute requiredPermission={Permission.DASHBOARD_VIEW}>
                  <Dashboard />
                </PermissionRoute>
              } />

              {/* Subscription Management - No subscription required */}
              <Route path="/subscription" element={
                <PermissionRoute requiredPermission={Permission.SUBSCRIPTIONS_VIEW}>
                  <SubscriptionManagement />
                </PermissionRoute>
              } />
              <Route path="/subscription/upgrade" element={
                <PermissionRoute requiredPermission={Permission.SUBSCRIPTIONS_MANAGE}>
                  <UpgradeSubscription />
                </PermissionRoute>
              } />

              {/* Protected routes - Subscription required */}
              <Route path="/purchase-orders" element={
                <ProtectedRoute requireSubscription={true}>
                  <PermissionRoute requiredPermission={Permission.PURCHASE_ORDERS_VIEW}>
                    <PurchaseOrders />
                  </PermissionRoute>
                </ProtectedRoute>
              } />
              <Route path="/purchase-orders/create" element={
                <ProtectedRoute requireSubscription={true}>
                  <PermissionRoute requiredPermission={Permission.PURCHASE_ORDERS_CREATE}>
                    <CreatePO />
                  </PermissionRoute>
                </ProtectedRoute>
              } />
              <Route path="/purchase-orders/:id" element={
                <ProtectedRoute requireSubscription={true}>
                  <PermissionRoute requiredPermission={Permission.PURCHASE_ORDERS_VIEW}>
                    <PODetails />
                  </PermissionRoute>
                </ProtectedRoute>
              } />

              <Route path="/invoices" element={
                <ProtectedRoute requireSubscription={true}>
                  <PermissionRoute requiredPermission={Permission.INVOICES_VIEW}>
                    <Invoices />
                  </PermissionRoute>
                </ProtectedRoute>
              } />
              <Route path="/invoices/create" element={
                <ProtectedRoute requireSubscription={true}>
                  <PermissionRoute requiredPermission={Permission.INVOICES_CREATE}>
                    <CreateInvoice />
                  </PermissionRoute>
                </ProtectedRoute>
              } />
              <Route path="/invoices/:id" element={
                <ProtectedRoute requireSubscription={true}>
                  <PermissionRoute requiredPermission={Permission.INVOICES_VIEW}>
                    <InvoiceDetails />
                  </PermissionRoute>
                </ProtectedRoute>
              } />
              <Route path="/invoices/:id/edit" element={
                <ProtectedRoute requireSubscription={true}>
                  <PermissionRoute requiredPermission={Permission.INVOICES_EDIT}>
                    <EditInvoice />
                  </PermissionRoute>
                </ProtectedRoute>
              } />

              <Route path="/users" element={
                <ProtectedRoute requireSubscription={true}>
                  <PermissionRoute requiredPermission={Permission.USERS_VIEW}>
                    <Users />
                  </PermissionRoute>
                </ProtectedRoute>
              } />
              <Route path="/users/create" element={
                <ProtectedRoute requireSubscription={true}>
                  <PermissionRoute requiredPermission={Permission.USERS_CREATE}>
                    <CreateUser />
                  </PermissionRoute>
                </ProtectedRoute>
              } />
              <Route path="/users/:id/edit" element={
                <ProtectedRoute requireSubscription={true}>
                  <PermissionRoute requiredPermission={Permission.USERS_EDIT}>
                    <EditUser />
                  </PermissionRoute>
                </ProtectedRoute>
              } />

              <Route path="/inventory" element={
                <ProtectedRoute requireSubscription={true}>
                  <PermissionRoute requiredPermission={Permission.INVENTORY_VIEW}>
                    <InventoryList />
                  </PermissionRoute>
                </ProtectedRoute>
              } />
              <Route path="/inventory/create" element={
                <ProtectedRoute requireSubscription={true}>
                  <PermissionRoute requiredPermission={Permission.INVENTORY_CREATE}>
                    <CreateInventory />
                  </PermissionRoute>
                </ProtectedRoute>
              } />
              <Route path="/inventory/:id/edit" element={
                <ProtectedRoute requireSubscription={true}>
                  <PermissionRoute requiredPermission={Permission.INVENTORY_EDIT}>
                    <EditInventory />
                  </PermissionRoute>
                </ProtectedRoute>
              } />

              <Route path="/retail-bills" element={
                <ProtectedRoute requireSubscription={true}>
                  <PermissionRoute requiredPermission={Permission.RETAIL_BILLS_VIEW}>
                    <RetailBillsList />
                  </PermissionRoute>
                </ProtectedRoute>
              } />
              <Route path="/retail-bills/create" element={
                <ProtectedRoute requireSubscription={true}>
                  <PermissionRoute requiredPermission={Permission.RETAIL_BILLS_CREATE}>
                    <CreateRetailBill />
                  </PermissionRoute>
                </ProtectedRoute>
              } />
              <Route path="/retail-bills/:id" element={
                <ProtectedRoute requireSubscription={true}>
                  <PermissionRoute requiredPermission={Permission.RETAIL_BILLS_VIEW}>
                    <ViewRetailBill />
                  </PermissionRoute>
                </ProtectedRoute>
              } />

              <Route path="/company/settings" element={
                <ProtectedRoute requireSubscription={true}>
                  <PermissionRoute requiredPermission={Permission.COMPANIES_SETTINGS}>
                    <CompanySettings />
                  </PermissionRoute>
                </ProtectedRoute>
              } />
              <Route path="/notifications" element={
                <ProtectedRoute requireSubscription={true}>
                  <PermissionRoute requiredPermission={Permission.NOTIFICATIONS_VIEW}>
                    <NotificationHistory />
                  </PermissionRoute>
                </ProtectedRoute>
              } />
              <Route path="/activities/:id" element={
                <ProtectedRoute requireSubscription={true}>
                  <PermissionRoute requiredPermission={Permission.ACTIVITIES_VIEW}>
                    <ActivityDetails />
                  </PermissionRoute>
                </ProtectedRoute>
              } />

              <Route path="/home" element={<Navigate to="/dashboard" replace />} />
            </Route>
          </Route>
        </Routes>
      </Router>
    </Provider>
  );
}

export default App;
